<template>
    <div>
        <!-- SMS LOGIN -->
        <div v-if="usingPhone" class="container">
          <div v-if="isApp" class="logo">
            <img src="/static/logo-white.svg" alt="engineering.com">
          </div>
          <div :class="subtitleClass"><span>{{ $t('phoneMessage') }}</span></div>
          <div class="field">
            <div class="control">
              <phone-input ref="phone" type="text" name="phone" v-model="phone" placeholder="Enter your phone number" :callback="sendSMS" :disabled="waiting"></phone-input>
              <div id="recaptcha"></div>
              <div v-if="error" class="help has-text-danger error">{{error}}</div>
            </div>
          </div>
          <div class="button is-primary is-full-width" :class="{'is-loading': waiting}" @click="sendSMS" :disabled="waiting || !recaptchaPassed || !this.phone">Next</div>
          <div class="lower">
            <div class="rates-message">{{ $t('phoneLogin.smsRates') }}</div>
            <div class="go-back"><a @click="goBackSMS">{{ $t('buildTemplate.back') }}</a></div>
          </div>
        </div>
        <!-- END SMS LOGIN -->

        <!-- SMS VERIFICATION -->
        <div v-if="sentSMS" class="container">
          <div v-if="isApp" class="logo">
              <img src="/static/logo-white.svg" alt="engineering.com">
          </div>
          <div :class="subtitleClass"><span>Enter the verification code you received</span></div>
          <div class="field full-width">
            <div class="control verification-input-container">
              <input type="text" class="input signup verification-input" maxlength="6" v-model="code" placeholder="Enter your verification code" @keyup.enter="verifySMS" :disabled="waiting" />
              <div v-if="error" class="help has-text-danger error">{{error}}</div>
            </div>
          </div>
          <div class="button is-primary is-full-width" :class="{'is-loading': waiting}" @click="verifySMS" :disabled="waiting">{{ $t('phoneLogin.verify') }}</div>
          <div class="lower gtm-sms-try-again">
            <p class="try-again-message">{{$t("login.getIt")}}<a @click="tryAgainSMS"> {{$t("login.tryAgain")}}</a></p>
            <div class="try-again-container"><div class="fine-print">{{ $t('phoneLogin.fewMinutes') }}</div></div>
          </div>
        </div>
        <!-- END SMS VERIFICATION -->
    </div>
</template>

<script>
import auth from '@/libs/auth'
import firebase from '@/libs/firebase'
import userAgent from '@/libs/user-agent'

import PhoneInput from '@/components/Inputs/Phone'
import VerificationCode from '@/components/Inputs/VerificationCode'
import signup from '@/mixins/signup'
import gaTrackingMixin from '@/mixins/ga-tracking'

const error = 3

export default {
  props: ['done', 'closeAppWebView', 'trackConversionInFacebook', 'goBack'],
  components: {
    PhoneInput,
    VerificationCode
  },
  mixins: [signup, gaTrackingMixin],
  data: function () {
    return {
      widgetid: null,
      view: this.viewSignup,
      code: '',
      user: null,
      error: '',
      phone: '',
      recaptcha: null,
      recaptchaPassed: false,
      phoneResult: null,
      waiting: false,
      appSMSModal: false,
      firebaseToken: null
    }
  },
  computed: {
    signupSource: function () {
      const source = this.redirect ? this.redirect.query.utm_source : this.$route.query.utm_source
      if (source) {
        return this.formatSource(source)
      }
      return ''
    },
    usingPhone: function () {
      return this.view === this.viewPhoneLogin || this.view === this.viewPhoneSignup
    },
    sentSMS: function () {
      return this.view === this.viewPhoneLoginVerificationCode || this.view === this.viewPhoneSignupVerificationCode
    },
    signingUp: function () {
      return this.view === this.viewSignup
    },
    signingUpPhone: function () {
      return this.view === this.viewPhoneSignup || this.view === this.viewPhoneSignupVerificationCode
    },
    isApp: userAgent.isApp,
    isError: function () {
      return this.state === error
    },
    redirect: function () {
      return this.$route.params.to
    },
    subtitleClass: function () {
      return this.isApp ? 'subtitle-app' : 'subtitle'
    },
    nextButtonClass: function () {
      if (this.isApp) {
        return {
          'button is-fullwidth gtm-sms-login-send': true,
          'is-loading': this.waiting
        }
      } else {
        return {
          'button is-primary is-full-width continue-button': true,
          'is-loading': this.waiting
        }
      }
    }
  },
  mounted: function () {
    this.view = this.viewSignup
    this.appSMSModal = this.$route.query.app || this.isApp
    this.showPhone()
  },
  methods: {
    show: function (view) {
      this.view = view
      this.$nextTick(() => { window.scrollTo({ top: 0, behavior: 'smooth' }) })
    },
    reset: function () {
      this.view = this.viewSignup
      this.code = ''
      this.user = null
      this.error = ''
      this.phone = ''
      this.recaptcha = null
      this.phoneResult = null
      this.recaptchaPassed = false
      this.firebaseToken = null
    },
    formatSource: function (source) {
      source = typeof source !== 'string' ? '{PATH}' : source
      if (this.$route.fullPath.indexOf('utm_source') >= 0 || !auth.getUtmSource()) {
        return source.replace('{PATH}', this.$route.fullPath)
      }
      return source.replace('{PATH}', this.$route.fullPath) + 'utm_source=' + auth.getUtmSource('utm_source')
    },
    showPhone: function () {
      this.show(this.signingUp ? this.viewPhoneSignup : this.viewPhoneLogin)
      this.error = ''
      this.$nextTick(() => {
        this.initRecaptcha()
      })
    },
    initRecaptcha: function () {
      this.recaptcha = new firebase.auth.RecaptchaVerifier('recaptcha', {
        size: 'normal',
        callback: () => { this.recaptchaPassed = true },
        'expired-callback': () => { }
      })
      this.widgetid = this.recaptcha.render()
    },
    resetRecaptcha: function () {
      if (window.grecaptcha) {
        window.grecaptcha.reset()
      }
    },
    sendSMS: function () {
      if (this.phone.length === 0 || !this.recaptchaPassed) {
        return
      }

      this.error = ''
      this.waiting = true
      firebase.auth().signInWithPhoneNumber(this.phone, this.recaptcha).then((confirm) => {
        this.phoneResult = confirm
        this.show(this.view === this.viewPhoneSignup ? this.viewPhoneSignupVerificationCode : this.viewPhoneLoginVerificationCode)
        this.waiting = false
      }, () => {
        this.waiting = false
        this.error = 'Error sending SMS. Check phone number and try again.'
      }).catch(() => {
        this.resetRecaptcha()
        this.waiting = false
        this.showPhone()
        this.error = 'Unable to send SMS. Check phone number and try again.'
      })
    },
    verifySMS: function () {
      this.error = ''
      this.waiting = true
      this.phoneResult.confirm(this.code).then(() => {
        this.firebaseLogin()
      }, () => {
        this.waiting = false
        this.error = 'Unable to verify. Check code and try again.'
      }).catch(() => {
        this.waiting = false
        this.resetRecaptcha()
        this.error = 'Unable to verify. Please try again.'
      })
    },
    firebaseLogin: function () {
      this.error = ''
      this.waiting = true
      this.getFirebaseToken().then((token) => {
        if (this.appSMSModal) {
          return this.close(true, token)
        }

        const data = {
          idToken: token,
          signupSource: this.signupSource,
          disableModals: true
        }

        this.$store.dispatch('firebaseLogin', data).then((response) => {
          auth.storeTokens(response.tokens)
          this.user = response.user
          this.waiting = false
          if (response.newUser) {
            this.trackSignupVerified('phone')
            this.trackConversionInFacebook()
            this.$emit('newUser', true)
          } else {
            this.trackSigninSuccessful('phone')
            this.$store.dispatch('setUtmSignupMethod', { signupMethod: 'phone' })
            this.$store.dispatch('trackUserSignup')
          }
          this.done(true)
        }, (error) => {
          this.waiting = false
          this.error = error.message
        })
      })
    },
    getFirebaseToken: function () {
      return new Promise((resolve) => {
        if (this.firebaseToken) {
          return resolve(this.firebaseToken)
        }

        firebase.auth().currentUser.getIdToken().then((token) => {
          this.firebaseToken = token
          resolve(token)
        })
      })
    },
    goBackSMS: function () {
      if (this.appSMSModal) {
        this.closeAppWebView('sms')
      } else {
        this.goBack()
      }
    },
    tryAgainSMS: function () {
      if (this.appSMSModal) {
        this.recaptchaPassed = false
        this.resetRecaptcha()
        this.show(this.$route.params.view === 'signup' ? this.viewPhoneSignup : this.viewPhoneLogin)
        this.$nextTick(() => {
          this.initRecaptcha()
        })
      } else {
        this.reset()
        this.done()
      }
    },
    close: function (success, data) {
      this.toggleIOS11Fix(false)

      if (this.appSMSModal) {
        this.closeAppWebView('sms', data)
      } else {
        this.resetRecaptcha()
      }

      this.$store.dispatch('hideLoginModal')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~styles/utils.scss';

.logo {
  margin: auto;
  img {
    width: 3rem;
  }
}

.section {
  height: auto;
  padding: 0;

  .container {
    margin: 1.3rem auto auto auto;
    display: flex;
    flex-direction: column;
    text-align: center;
    align-items: center;
    width: 400px;
    padding-bottom: 1rem;
    @include mobile {
      width: 300px;
      max-width: 300px;
    }
    @media screen and (max-height: 375px) {
      margin-top: auto;
    }
  }
}

.subtitle {
    width: inherit;
    color: #4A4A4A;
    margin: 0;
    padding: 0 0 1.5rem 0;
    font-size: 1rem;
    line-height: 1.4rem;
    @include mobile{
        font-size: 1rem;
    }
}

.rates-message {
  font-family: 'Roboto';
  font-weight: 300;
  font-size: 0.9rem;
  padding: 0.3rem 0 0.1rem;
}

.go-back {
  font-size: 0.9rem;
  font-weight: 300;
}

.help {
  max-width: 304px;
}

.subtitle-app {
  color: #fff;
  margin: 1rem auto;
  line-height: 1.5rem;
  @include mobile{
    font-size: 1rem;
    line-height: 1.25rem;
  }
  span{
    display: block;
  }
}

.full-width {
  width: 100%;
}

.input {
  box-shadow: none;
  max-width: 304px;
}

.verification-input-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;

  .verification-input {
    max-width: 304px;
    text-align: center;
    font-family: 'Roboto';
    height: 50px;
    border: none;
    border-radius: 4px;
    &::placeholder {
      font-weight: 300;
      color: #565656;
      font-family: 'Roboto';
      opacity: 1;
    }
  }

}

.next {
  margin: 1rem auto;
  padding: 0 3rem;
  width: 100%;
}

.error {
  text-align: center;
  color: #2e211d;
  padding-bottom: 0.4rem;
  background: #e9e9e926;
  padding:0.7rem;
  border-radius: 5px;
  .icon {
    margin-right: 0.5rem;
  }
}

.try-again-container{
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 5px;
  font-family: 'Roboto';
  font-weight: 300;
  font-size: 0.9rem;
}

.try-again-message {
  color: #3F3F3F;
  font-weight: 600;
}

.fine-print{
  font-size: 0.75rem;
}

.lower {
  width: 100%;
  padding: 1rem 0 0 0;
  margin-bottom: 1.3rem;
}

.button {
  border-radius: 0;
  padding: 0 2rem;
}

</style>
<style lang="scss">
  body.ios-modal-fix {
    position: fixed;
  }

  #recaptcha {
    min-height: 78px;
    > div > div {
      margin: auto;
    }
  }
</style>
